import apiClient from './apiClient';
import type {
  ReporteJugador,
  CreateReporteJugadorRequest,
  ReportesJugadoresResponse,
  PlayerSearchResult,
  CompetitionSearchResult,
  TeamSearchResult
} from '../types/reportesJugadores';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * Service for Reportes Jugadores API calls
 */
class ReportesJugadoresService {
  private readonly baseUrl = '/reportes-jugadores';

  /**
   * Obtener lista de reportes con paginación
   */
  async getReportes(page: number = 1, perPage: number = 20, search: string = ''): Promise<ApiResponse<ReportesJugadoresResponse>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        ...(search && { search })
      });

      const response = await apiClient.get<ReportesJugadoresResponse>(`${this.baseUrl}?${params}`);
      
      return {
        success: true,
        data: response.data,
        message: 'Reportes obtenidos exitosamente'
      };
    } catch (error) {
      console.error('Error fetching reportes:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error al obtener los reportes'
      };
    }
  }

  /**
   * Obtener un reporte específico por ID
   */
  async getReporte(reportId: number): Promise<ApiResponse<ReporteJugador>> {
    try {
      const response = await apiClient.get<{ success: boolean; data: ReporteJugador }>(`${this.baseUrl}/${reportId}`);
      
      return {
        success: true,
        data: response.data.data,
        message: 'Reporte obtenido exitosamente'
      };
    } catch (error) {
      console.error('Error fetching reporte:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error al obtener el reporte'
      };
    }
  }

  /**
   * Crear un nuevo reporte
   */
  async createReporte(reporteData: CreateReporteJugadorRequest): Promise<ApiResponse<ReporteJugador>> {
    try {
      const response = await apiClient.post<{ success: boolean; data: ReporteJugador }>(`${this.baseUrl}`, reporteData);
      
      return {
        success: true,
        data: response.data.data,
        message: 'Reporte creado exitosamente'
      };
    } catch (error) {
      console.error('Error creating reporte:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error al crear el reporte'
      };
    }
  }

  /**
   * Buscar jugadores para autocomplete
   */
  async searchPlayers(query: string): Promise<ApiResponse<PlayerSearchResult[]>> {
    try {
      if (!query || query.length < 2) {
        return {
          success: true,
          data: []
        };
      }

      const response = await apiClient.get<{ success: boolean; data: PlayerSearchResult[] }>(
        `${this.baseUrl}/search-players?q=${encodeURIComponent(query)}`
      );
      
      return {
        success: true,
        data: response.data.data || [],
        message: 'Búsqueda completada'
      };
    } catch (error) {
      console.error('Error searching players:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Error en la búsqueda de jugadores'
      };
    }
  }

  /**
   * Buscar competiciones para autocomplete
   * Si no hay query, devuelve todas las competiciones
   */
  async searchCompetitions(query: string = ''): Promise<ApiResponse<CompetitionSearchResult[]>> {
    try {
      const url = query 
        ? `${this.baseUrl}/search-competitions?q=${encodeURIComponent(query)}`
        : `${this.baseUrl}/search-competitions`;

      const response = await apiClient.get<{ success: boolean; data: CompetitionSearchResult[] }>(url);
      
      return {
        success: true,
        data: response.data.data || [],
        message: 'Búsqueda completada'
      };
    } catch (error) {
      console.error('Error searching competitions:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Error en la búsqueda de competiciones'
      };
    }
  }

  /**
   * Buscar equipos para autocomplete
   */
  async searchTeams(query: string): Promise<ApiResponse<TeamSearchResult[]>> {
    try {
      if (!query || query.length < 2) {
        return {
          success: true,
          data: []
        };
      }

      const response = await apiClient.get<{ success: boolean; data: TeamSearchResult[] }>(
        `${this.baseUrl}/search-teams?q=${encodeURIComponent(query)}`
      );
      
      return {
        success: true,
        data: response.data.data || [],
        message: 'Búsqueda completada'
      };
    } catch (error) {
      console.error('Error searching teams:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Error en la búsqueda de equipos'
      };
    }
  }
}

export const reportesJugadoresService = new ReportesJugadoresService();
